/**
 * STOREFRONT APPEARANCE CONTRACT (customer-delivery-webpage)
 *
 * Shape the portal EDITS and the customer storefront CONSUMES. Does not paint
 * the portal itself — panel colors stay in `config/theme`. Mixing the two would
 * retheme the merchant's workbench when changing the storefront.
 *
 * Versioned because today's payload lives in browser `localStorage` and tomorrow
 * on an endpoint: old data, new code. Every read goes through
 * {@link normalizeStorefrontAppearance}.
 *
 * MIRRORED byte-for-byte with the portal copy — no monorepo, so `diff` must be
 * empty. Portal-only labels (`*_LABELS`, `*_PRESETS`) travel along on purpose.
 */

/** Persisted format version. Bump together with a `normalize` migration. */
export const STOREFRONT_APPEARANCE_VERSION = 1;

/**
 * Shared size scale for section and card — one "medium" choice, two controls.
 */
export type StorefrontSizeToken = "compact" | "medium" | "large";
export type StorefrontSizeRounded = "compact" | "medium" | "large";
export const STOREFRONT_SIZES = ["compact", "medium", "large"] as const;

export const STOREFRONT_SIZE_LABELS: Record<StorefrontSizeToken, string> = {
  compact: "Compacto",
  medium: "Médio",
  large: "Grande",
};

/**
 * Card corner radius in PX (not `rem`): matches existing `rounded-[5px]` on
 * offer/rail/reorder/category cards. Radius is not type — it must not scale
 * when either side redefines root font size. `medium` is that 5px default;
 * `compact` is a sharp corner, not "5px smaller".
 */
export const STOREFRONT_ROUNDED_PX: Record<StorefrontSizeRounded, number> = {
  compact: 0,
  medium: 5,
  large: 10,
};

export const STOREFRONT_ROUNDEDS = ["compact", "medium", "large"] as const;

/**
 * Corner labels. Own vocabulary (not Compact/Medium/Large): merchants pick
 * box shape, not corner size.
 */
export const STOREFRONT_ROUNDED_LABELS: Record<StorefrontSizeRounded, string> = {
  compact: "Quadrado",
  medium: "Normal",
  large: "Arredondado",
};

/**
 * Title band behind a block heading. Grouped (not a lone boolean) because the
 * band has its OWN corner — a separate page piece; a straight band over rounded
 * cards is a valid choice merchants must be able to make.
 */
export type StorefrontTitleCard = {
  /** Off: title sits loose on the background — the historical look. */
  visible: boolean;
  /** Band corner, independent of the block's card corner. */
  rounded: StorefrontSizeRounded;
};

/**
 * Section title size in `rem` (storefront typography unit; bridges 14px root vs
 * portal without conversion). Compact is today's `text-base` on rail `h2`s —
 * the no-op default. Uses the portal three-step scale like
 * {@link STOREFRONT_IMAGE_SIZE_LABELS}.
 */
export const STOREFRONT_TITLE_REM: Record<StorefrontSizeToken, number> = {
  compact: 1,
  medium: 1.5,
  large: 2,
};

export const STOREFRONT_TITLE_LABELS: Record<StorefrontSizeToken, string> = {
  compact: "Pequeno",
  medium: "Médio",
  large: "Grande",
};

/**
 * Category bar size in `rem`. Shorter than title on purpose: a horizontal row
 * of uppercase names; title-sized type would fit only two and force scrolling.
 * Compact is today's `text-base`.
 */
export const STOREFRONT_CATEGORY_REM: Record<StorefrontSizeToken, number> = {
  compact: 1,
  medium: 1.25,
  large: 1.5,
};

export const STOREFRONT_CATEGORY_LABELS = STOREFRONT_TITLE_LABELS;

/**
 * Carousel arrow size in `rem`. Medium is today's `w-9 h-9` (2.25rem) on both
 * carousels. Short scale: past ~3rem the arrow covers the product photo.
 */
export const STOREFRONT_ARROW_REM: Record<StorefrontSizeToken, number> = {
  compact: 1.75,
  medium: 2.25,
  large: 2.75,
};

export const STOREFRONT_ARROW_LABELS = STOREFRONT_TITLE_LABELS;

/**
 * Coupon art format. Merchant-uploaded image (not CSS); format is the export
 * aspect. `large` is today's 16:9 piece; `small` is a squarer ticket crop.
 */
export type StorefrontCouponArt = "small" | "large";

export const STOREFRONT_COUPON_ARTS = ["small", "large"] as const;

export const STOREFRONT_COUPON_ART_LABELS: Record<StorefrontCouponArt, string> = {
  small: "Pequena",
  large: "Grande",
};

/** Aspect ratio (width ÷ height) for each art format. */
export const STOREFRONT_COUPON_ART_ASPECT: Record<StorefrontCouponArt, number> = {
  small: 3 / 2,
  large: 16 / 9,
};

/**
 * Fraction of the column ART occupies, after format discount. Size step always
 * applies; small art shrinks via {@link COUPON_SMALL_ART_SCALE}. `widthRatio`
 * comes from the caller (real column vs mockup frame).
 */
export function couponArtWidthRatio(art: StorefrontCouponArt, widthRatio: number): number {
  return art === "small" ? widthRatio * COUPON_SMALL_ART_SCALE : widthRatio;
}

/** How the coupon shows in the carousel: uploaded art or built card. */
export type StorefrontCouponDisplay = "photo" | "card";

export const STOREFRONT_COUPON_DISPLAYS = ["photo", "card"] as const;

export const STOREFRONT_COUPON_DISPLAY_LABELS: Record<StorefrontCouponDisplay, string> = {
  photo: "Foto",
  card: "Card",
};

/**
 * BUILT COUPON (`couponDisplay: "card"`)
 *
 * Different from PHOTO art — own metrics. Photo is an upload (aspect is an
 * export rule); the card is DRAWN (chip, seam, code, minimum) and needs a
 * small card, not a banner. HEIGHT is primary; width follows aspect so size
 * steps change the whole piece.
 */
export const COUPON_CARD_HEIGHT_REM: Record<StorefrontSizeToken, number> = {
  compact: 6.5,
  medium: 8,
  large: 10,
};

/**
 * Built-coupon width ÷ height. Near-square (not landscape like art): content
 * fills the left half; extra width is empty space, not information.
 */
export const COUPON_CARD_ASPECT = 1.45;

/**
 * Built-coupon width in `rem`. Rounded so float noise does not land in `style`.
 */
export function couponCardWidthRem(size: StorefrontSizeToken): number {
  return Math.round(COUPON_CARD_HEIGHT_REM[size] * COUPON_CARD_ASPECT * 100) / 100;
}

/**
 * Where the seam cuts the built coupon, as a HEIGHT fraction. Fixed position —
 * notches must meet the line; a content-relative seam would drift.
 */
export const COUPON_SEAM_RATIO = 0.64;

/**
 * Notch diameter as a height fraction (not px) so it scales across storefront
 * sizes and the portal mockup.
 */
export const COUPON_NOTCH_RATIO = 0.13;

/**
 * How much SMALL art (3:2) shrinks vs large (16:9) at the SAME size step.
 * Same width would make 3:2 the tallest home piece; shrinking preserves three
 * size steps and keeps large 3:2 shorter than large 16:9.
 */
export const COUPON_SMALL_ART_SCALE = 0.76;

/**
 * Configurable home sections. Keys are the storefront contract:
 * - `reorder` → ReorderLastOrderCard
 * - `featured` → featured items
 * - `promotions` → ItemOfferCard
 * - `coupons` → active store coupons
 * - `bestsellers` → top sellers from store orders
 * - `benefits` → the merchant's active loyalty mechanics (BenefitsCarousel)
 */
export type StorefrontSectionKey =
  | "reorder"
  | "featured"
  | "promotions"
  | "coupons"
  | "bestsellers"
  | "benefits";

export const STOREFRONT_SECTION_KEYS = [
  "reorder",
  "featured",
  "promotions",
  "coupons",
  "bestsellers",
  "benefits",
] as const;

/**
 * What the merchant panel needs about a section. `editable`/`contentHint` is a
 * UNION: non-editable sections must explain content source; editable ones must
 * not carry that hint — otherwise a mute panel looks like a missing control.
 */
type StorefrontSectionMeta = {
  label: string;
  description: string;
  /**
   * Section cards have ITEM PHOTOS, so the merchant can hide them
   * ({@link StorefrontSection.imageVisible}). Coupons are excluded — there the
   * section IS the image (uploaded art), not a photo beside text.
   */
  itemPhoto: boolean;
  /**
   * Section shows coupon ART, so it picks format ({@link StorefrontSection.couponArt}).
   * Inverse of {@link itemPhoto}: photo-beside-text has no art; art sections have no photo toggle.
   */
  couponArt: boolean;
} & (
  | {
      /** Section has CONTENT the merchant chooses. */
      editable: true;
    }
  | {
      /**
       * Content is NOT merchant-chosen (reorder = prior orders; bestsellers =
       * store-wide counts). No control to offer.
       */
      editable: false;
      /**
       * Content source shown to the merchant. Lives in the MAP (not the panel)
       * because each section's reason differs — per-customer vs store-wide.
       */
      contentHint: string;
    }
);

export const STOREFRONT_SECTION_LABELS: Record<StorefrontSectionKey, StorefrontSectionMeta> = {
  reorder: {
    label: "Peça novamente",
    description: "Últimos pedidos do cliente, prontos para repetir.",
    editable: false,
    itemPhoto: true,
    couponArt: false,
    contentHint:
      "Os itens desta seção vêm dos pedidos anteriores de cada cliente, um a um, não há lista para escolher aqui.",
  },
  featured: {
    label: "Itens em destaque",
    description: "Vitrine dos produtos que você quer empurrar primeiro.",
    editable: true,
    itemPhoto: true,
    couponArt: false,
  },
  promotions: {
    label: "Promoções",
    description: "Itens com preço promocional ativo.",
    editable: true,
    itemPhoto: true,
    couponArt: false,
  },
  coupons: {
    label: "Cupons disponíveis",
    description: "Cupons ativos, prontos para o cliente aplicar na sacola.",
    editable: true,
    itemPhoto: false,
    couponArt: true,
  },
  bestsellers: {
    label: "Mais pedidos",
    description: "Os itens que mais saem, contados sobre os pedidos da loja.",
    editable: false,
    itemPhoto: true,
    couponArt: false,
    contentHint:
      "Os itens desta seção são contados sobre os pedidos de todos os clientes da loja, não há lista para escolher aqui.",
  },
  benefits: {
    label: "Benefícios da loja",
    description:
      "As fidelidades ativas, em carrossel: programa de pontos, compre mais e ganhe, promoções por item e recorrência.",
    editable: false,
    itemPhoto: false,
    couponArt: false,
    contentHint:
      "Os cards desta seção vêm das fidelidades ativas em Configurações → Fidelidade, cada uma com a sua vigência. Sem fidelidade ativa, a seção não aparece.",
  },
};

export type StorefrontSection = {
  key: StorefrontSectionKey;
  /** Off: section is not rendered (not even empty). */
  visible: boolean;
  size: StorefrontSizeToken;
  /**
   * Show item photo on THIS rail's cards? Off: name/price/CTA only, and the
   * card SHRINKS by photo width (more items per view). Per section, not home-
   * wide. Ignored when `itemPhoto: false` (coupons — section is the art).
   */
  imageVisible: boolean;
  /**
   * Coupon art format. Read only where `couponArt: true` (coupons), same status
   * as {@link imageVisible} — uniform field, map decides who asks.
   */
  couponArt: StorefrontCouponArt;
  /**
   * Photo (uploaded art) or card (code + discount). Coupons only; missing in
   * old payloads falls back to photo.
   */
  couponDisplay: StorefrontCouponDisplay;
  /**
   * Corner of THIS section's card — box only; inner photo and built coupon keep
   * their own shape. On coupons, only for `couponDisplay: "photo"`.
   */
  rounded: StorefrontSizeRounded;
  /**
   * Title band for THIS section, with its own corner. Per section (unlike
   * home-wide {@link StorefrontAppearance.titleSize}): band is emphasis weight.
   */
  titleCard: StorefrontTitleCard;
};

/**
 * Seven SOLID color roles — roles, not swatches: `primary` action, `secondary`
 * support, `border` field stroke — never "the orange" or "the gray".
 */
export const STOREFRONT_COLOR_ROLES = [
  "background",
  "primary",
  "secondary",
  "cardBackground",
  "border",
  "cardText",
  "text",
] as const;

export type StorefrontColorRole = (typeof STOREFRONT_COLOR_ROLES)[number];

/**
 * TITLE BAND roles. Each follows the same-named card role while `null`; once
 * the merchant pins a color, the band stops tracking the card.
 */
export const STOREFRONT_TITLE_CARD_COLOR_ROLES = ["titleCardBackground", "titleCardText"] as const;

export type StorefrontTitleCardColorRole = (typeof STOREFRONT_TITLE_CARD_COLOR_ROLES)[number];

/** Seven solid roles plus two optional title-band roles. */
export type StorefrontColors = Record<StorefrontColorRole, string> &
  Record<StorefrontTitleCardColorRole, string | null>;

/** Card role each band role borrows until it has its own color. */
export const STOREFRONT_TITLE_CARD_COLOR_SOURCE: Record<StorefrontTitleCardColorRole, StorefrontColorRole> = {
  titleCardBackground: "cardBackground",
  titleCardText: "cardText",
};

/** Solid band colors per role: merchant's, or the card's while unset. */
export function resolveTitleCardColors(colors: StorefrontColors): Record<StorefrontTitleCardColorRole, string> {
  return {
    titleCardBackground: colors.titleCardBackground ?? colors.cardBackground,
    titleCardText: colors.titleCardText ?? colors.cardText,
  };
}

/** True while both band roles still follow the card. */
export function titleCardFollowsCard(colors: StorefrontColors): boolean {
  return STOREFRONT_TITLE_CARD_COLOR_ROLES.every((role) => colors[role] === null);
}

/**
 * Band roles follow the card again, or are PINNED to the card's current color
 * so unchecking "same as cards" opens pickers on the band's prior color.
 */
export function withTitleCardFollowingCard(colors: StorefrontColors, follow: boolean): StorefrontColors {
  const next = { ...colors };
  for (const role of STOREFRONT_TITLE_CARD_COLOR_ROLES) {
    next[role] = follow ? null : colors[STOREFRONT_TITLE_CARD_COLOR_SOURCE[role]];
  }
  return next;
}

export const STOREFRONT_COLOR_LABELS: Record<StorefrontColorRole, { label: string; description: string }> = {
  background: {
    label: "Fundo da loja",
    description: "Cor da página inteira, atrás de tudo.",
  },
  primary: {
    label: "Cor primária",
    description: "Ações principais: botões, em destaque, seleção.",
  },
  secondary: {
    label: "Cor secundária",
    description: "Apoio: selos, preço, etiquetas de promoção, realces.",
  },
  cardBackground: {
    label: "Fundo dos cards",
    description: "Superfície dos cards de produto e das seções.",
  },
  border: {
    label: "Borda",
    description: "Campos, botões e o contorno da busca.",
  },
  cardText: {
    label: "Texto dos cards",
    description: "Nome e descrição do produto dentro do card.",
  },
  text: {
    label: "Texto da loja",
    description: "Títulos e textos fora dos cards.",
  },
};

export const STOREFRONT_TITLE_CARD_COLOR_LABELS: Record<
  StorefrontTitleCardColorRole,
  { label: string; description: string }
> = {
  titleCardBackground: {
    label: "Fundo da faixa de título",
    description: "A faixa atrás do nome da seção e da categoria.",
  },
  titleCardText: {
    label: "Texto da faixa de título",
    description: "O nome da seção ou da categoria sobre a faixa.",
  },
};

/**
 * Product photo shape inside the card — the decision that most changes the menu.
 * - `square`: 1:1 thumbnail beside text (list default)
 * - `wide`: 16:9 on top (showcase)
 * - `round`: circular thumbnail
 */
export type StorefrontImageShape = "square" | "wide" | "round";

export const STOREFRONT_IMAGE_SHAPES = ["square", "wide", "round"] as const;

export const STOREFRONT_IMAGE_SHAPE_LABELS: Record<StorefrontImageShape, string> = {
  square: "Quadrada",
  wide: "Panorâmica",
  round: "Redonda",
};

export type StorefrontItemImage = {
  /**
   * Off: text-only card. A store without good photos sells better without them
   * than with bad ones — merchant choice, not a forced default.
   */
  visible: boolean;
  size: StorefrontSizeToken;
  shape: StorefrontImageShape;
};

export const STOREFRONT_IMAGE_SIZE_LABELS: Record<StorefrontSizeToken, string> = {
  compact: "Pequena",
  medium: "Média",
  large: "Grande",
};

/**
 * How PRICE presents on the card. Grouped (not a lone boolean) like
 * {@link StorefrontItemImage}: more than one decision; a group lets the next in
 * without reopening the contract root.
 */
export type StorefrontPrice = {
  /**
   * Show the "from" label before the value when option groups can raise price.
   * Announced number is the ENTRY price. Default on — hiding it on a growing
   * price is costly to fix after the customer only notices in the bag.
   */
  showFromLabel: boolean;
};

export const STOREFRONT_PRICE_LABELS: Record<keyof StorefrontPrice, { label: string; description: string }> = {
  showFromLabel: {
    label: 'Mostrar "A partir de"',
    description: "Nos itens com opções, avisa que o valor é o de entrada e pode subir conforme o cliente escolhe.",
  },
};

/**
 * MENU block — categories and items below the rails. Grouped like
 * {@link StorefrontItemImage}. {@link StorefrontAppearance.cardSize} stays
 * outside (payload migration would buy only symmetry).
 */
export type StorefrontMenu = {
  /** Menu card corner, same reach as the section corner. */
  rounded: StorefrontSizeRounded;
  /** Band behind the category name, with its own corner. */
  titleCard: StorefrontTitleCard;
};

/**
 * CATEGORY LIST menu (`menuMode: CATEGORY_LIST`): grid of full-bleed photo
 * cards; opening a category shows items. Own group vs product list — different
 * decisions. Which mode draws is channel `menuMode`, not appearance.
 */
export type StorefrontCategoryList = {
  /**
   * Card size: desktop columns (compact 3, medium 2, large 1) and height.
   * Mobile is always one per row; size only drives height.
   */
  cardSize: StorefrontSizeToken;
  /** Category card corner. */
  rounded: StorefrontSizeRounded;
  /** Category description under the name. */
  showDescription: boolean;
};

export const STOREFRONT_CATEGORY_LIST_LABELS = {
  cardSize: {
    label: "Tamanho dos cards",
    description:
      "Compacto cabe três categorias por linha no computador; grande dá uma só, mais alta. No celular, o tamanho manda na altura.",
  },
  rounded: {
    label: "Canto do card",
    description:
      "Quadrado deixa o canto vivo. Normal é o canto que a loja já usa. Arredondado marca mais a caixa.",
  },
  showDescription: {
    label: "Mostrar descrição",
    description: "A descrição da categoria, em uma linha, abaixo do nome.",
  },
} as const;

/**
 * Home header density. Two full layouts, not one shrunk — text vs logo placement.
 * - `detailed`: name band, address, ETA, delivery, minimum, open/closed bar
 * - `compact`: identity card beside the logo (status, city, "see more")
 * Logo bite on the banner is unchanged between modes.
 */
export type StorefrontHeaderDensity = "detailed" | "compact";

export const STOREFRONT_HEADER_DENSITIES = ["detailed", "compact"] as const;

export const STOREFRONT_HEADER_DENSITY_LABELS: Record<StorefrontHeaderDensity, { label: string; description: string }> =
  {
    detailed: {
      label: "Detalhado",
      description: "Endereço completo, tempo, entrega, pedido mínimo e a barra de aberta/fechada.",
    },
    compact: {
      label: "Compacto",
      description: "Nome, status e cidade ao lado da logo. Sem rua, tempo, entrega nem pedido mínimo.",
    },
  };

export type StorefrontHeader = {
  /**
   * Sole header TEXT decision on purpose. ETA/delivery/minimum come from here —
   * a separate boolean would allow "compact with all three" that neither layout
   * supports.
   */
  density: StorefrontHeaderDensity;
  /**
   * Store COVER photo above the logo. Own switch (orthogonal to
   * {@link StorefrontHeaderDensity}). Off: header starts on the card surface;
   * logo loses the banner bite. Default on — historical header.
   */
  showBanner: boolean;
};

export const STOREFRONT_HEADER_LABELS = {
  density: {
    label: "Cabeçalho da loja",
    description: "Quanto o cabeçalho mostra: endereço e infos de entrega, ou só nome, status e cidade.",
  },
  showBanner: {
    label: "Mostrar o banner da loja",
    description: "A foto de capa acima da logo. Desligada, o cabeçalho começa direto no nome da loja.",
  },
} as const;

/**
 * SUPPORT BUTTON
 *
 * Floating chip to the merchant's WhatsApp. Named `support` (the thing), not
 * the widget/vendor. Phone number is `contactPhones.whatsappNumber` — not
 * appearance. Color is always `colors.secondary`. No number ⇒ no button.
 */
export type StorefrontSupportSide = "left" | "right";

export const STOREFRONT_SUPPORT_SIDES = ["left", "right"] as const;

export const STOREFRONT_SUPPORT_SIDE_LABELS: Record<StorefrontSupportSide, string> = {
  left: "Esquerda",
  right: "Direita",
};

/**
 * What the chip shows. Size and anatomy are SEPARATE — tying icon-only to the
 * smallest step would remove a small button WITH a label.
 */
export type StorefrontSupportContent = "iconLabel" | "icon" | "label";

export const STOREFRONT_SUPPORT_CONTENTS = ["iconLabel", "icon", "label"] as const;

export const STOREFRONT_SUPPORT_CONTENT_LABELS: Record<StorefrontSupportContent, string> = {
  iconLabel: "Ícone + texto",
  icon: "Ícone",
  label: "Texto",
};

/**
 * Chip step on the storefront's existing Button scale (`xs`/`sm`/`md`) — step
 * name, never a px height.
 */
export const STOREFRONT_SUPPORT_BUTTON_SIZE: Record<StorefrontSizeToken, "xs" | "sm" | "md"> = {
  compact: "xs",
  medium: "sm",
  large: "md",
};

/**
 * Pre-written message cap in characters. Travels percent-encoded in `?text=`;
 * mobile browsers truncate long URLs without warning — cut here so merchants
 * see the limit while editing.
 */
export const STOREFRONT_SUPPORT_MESSAGE_MAX = 300;

/**
 * Customer NAME placeholder in the message. Plain domain word, not `${...}`
 * (dollar reads as price). Without a known name the token and orphan spacing
 * are removed.
 */
export const STOREFRONT_SUPPORT_NAME_TOKEN = "{nome}";

export type StorefrontSupport = {
  /**
   * Off: no button. Defaults OFF — otherwise every published store would gain a
   * new chip over the menu unasked.
   */
  visible: boolean;
  /**
   * Chip size step. `medium` is the default.
   */
  size: StorefrontSizeToken;
  /** Icon, label, or both. Icon-only draws SQUARE; `rounded` sets the corner. */
  content: StorefrontSupportContent;
  /** Screen corner where the chip sits while browsing. */
  side: StorefrontSupportSide;
  /** Chip corner on the SAME scale as cards — not a new vocabulary. */
  rounded: StorefrontSizeRounded;
  /**
   * Prefill when chat opens. EMPTY means empty (no `?text=`). A hidden factory
   * default would leave text after the merchant clears the field.
   */
  message: string;
};

export const STOREFRONT_SUPPORT_LABELS = {
  visible: {
    label: "Botão de dúvidas",
    description: "Uma pastilha flutuante em todas as telas da loja, que abre seu WhatsApp em outra aba.",
  },
  size: {
    label: "Tamanho da pastilha",
    description: "Quanto de tela o botão ocupa por cima do cardápio.",
  },
  content: {
    label: "Conteúdo da pastilha",
    description: "Sem o texto, o botão vira um quadrado com o ícone do WhatsApp.",
  },
  side: {
    label: "Lado da tela",
    description: "Em qual canto a pastilha fica enquanto o cliente navega.",
  },
  rounded: {
    label: "Canto da pastilha",
    description: "A mesma escala de canto que os cards da loja usam.",
  },
  message: {
    label: "Mensagem já escrita",
    description: "O que aparece digitado quando a conversa abre. Em branco, o cliente escreve do zero.",
  },
  nameToken: {
    label: "Inserir nome do cliente",
    description:
      "O cliente só é identificado no fim do carrinho. Antes disso o nome não existe, e a frase sai sem ele.",
  },
} as const;

export type StorefrontAppearance = {
  version: number;
  /** Array ORDER is home render order. */
  sections: StorefrontSection[];
  /**
   * Title size for ALL sections. Page rhythm — mixed sizes read as misalignment.
   */
  titleSize: StorefrontSizeToken;
  /** Category bar size (`CategoriesNavigation`). */
  categoryNavSize: StorefrontSizeToken;
  /** Carousel prev/next arrow size. */
  arrowSize: StorefrontSizeToken;
  cardSize: StorefrontSizeToken;
  /** Product photo INSIDE the card, independent of card size. */
  itemImage: StorefrontItemImage;
  /** How the price presents on the card. */
  price: StorefrontPrice;
  /** Header density (detailed or compact). */
  header: StorefrontHeader;
  /** Menu block below the rails. */
  menu: StorefrontMenu;
  /** Same block when the channel menu is category-list mode. */
  categoryList: StorefrontCategoryList;
  /** Floating support button on every storefront screen. */
  support: StorefrontSupport;
  colors: StorefrontColors;
};

export const DEFAULT_STOREFRONT_APPEARANCE: StorefrontAppearance = {
  version: STOREFRONT_APPEARANCE_VERSION,
  sections: [
    {
      key: "reorder",
      visible: true,
      size: "medium",
      imageVisible: true,
      couponArt: "large",
      couponDisplay: "photo",
      rounded: "medium",
      titleCard: { visible: false, rounded: "medium" },
    },
    {
      key: "coupons",
      visible: true,
      size: "medium",
      imageVisible: true,
      couponArt: "large",
      couponDisplay: "photo",
      rounded: "medium",
      titleCard: { visible: false, rounded: "medium" },
    },
    {
      key: "promotions",
      visible: true,
      size: "medium",
      imageVisible: true,
      couponArt: "large",
      couponDisplay: "photo",
      rounded: "medium",
      titleCard: { visible: false, rounded: "medium" },
    },
    {
      key: "featured",
      visible: true,
      size: "medium",
      imageVisible: true,
      couponArt: "large",
      couponDisplay: "photo",
      rounded: "medium",
      titleCard: { visible: false, rounded: "medium" },
    },
    {
      key: "bestsellers",
      visible: true,
      size: "medium",
      imageVisible: true,
      couponArt: "large",
      couponDisplay: "photo",
      rounded: "medium",
      titleCard: { visible: false, rounded: "medium" },
    },
    {
      key: "benefits",
      visible: false,
      size: "medium",
      imageVisible: true,
      couponArt: "large",
      couponDisplay: "photo",
      rounded: "medium",
      titleCard: { visible: false, rounded: "medium" },
    },
  ],
  titleSize: "compact",
  categoryNavSize: "compact",
  arrowSize: "medium",
  cardSize: "medium",
  itemImage: {
    visible: true,
    size: "medium",
    shape: "square",
  },
  price: {
    showFromLabel: true,
  },
  header: {
    density: "detailed",
    showBanner: true,
  },
  menu: {
    rounded: "medium",
    titleCard: { visible: false, rounded: "medium" },
  },
  categoryList: {
    cardSize: "medium",
    rounded: "medium",
    showDescription: true,
  },
  support: {
    visible: false,
    size: "medium",
    content: "iconLabel",
    side: "right",
    rounded: "medium",
    message: "",
  },
  colors: {
    background: "#F5F5F5",
    primary: "#E5770B",
    secondary: "#15803D",
    cardBackground: "#FFFFFF",
    border: "#C2C2C2",
    cardText: "#252525",
    text: "#252525",
    titleCardBackground: null,
    titleCardText: null,
  },
};

/**
 * Starting palettes. Not behavioral "themes" — one-shot color sets so merchants
 * do not start from seven empty pickers.
 */
export type StorefrontColorPreset = {
  id: string;
  label: string;
  colors: StorefrontColors;
};

export const STOREFRONT_COLOR_PRESETS: StorefrontColorPreset[] = [
  {
    id: "clara",
    label: "Clara",
    colors: DEFAULT_STOREFRONT_APPEARANCE.colors,
  },
  {
    id: "escura",
    label: "Escura",
    colors: {
      background: "#111827",
      primary: "#F59E0B",
      secondary: "#38BDF8",
      cardBackground: "#1F2937",
      border: "#4B5563",
      cardText: "#F9FAFB",
      text: "#F9FAFB",
      titleCardBackground: null,
      titleCardText: null,
    },
  },
  {
    id: "vibrante",
    label: "Vibrante",
    colors: {
      background: "#FFF7ED",
      primary: "#DB2777",
      secondary: "#7C3AED",
      cardBackground: "#FFFFFF",
      border: "#E9D5FF",
      cardText: "#1F2937",
      text: "#4C1D95",
      titleCardBackground: null,
      titleCardText: null,
    },
  },
  {
    id: "sobria",
    label: "Sóbria",
    colors: {
      background: "#FFFFFF",
      primary: "#047857",
      secondary: "#0F766E",
      cardBackground: "#F8FAFC",
      border: "#CBD5E1",
      cardText: "#0F172A",
      text: "#0F172A",
      titleCardBackground: null,
      titleCardText: null,
    },
  },
];

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR.test(value.trim());
}

function readSize(value: unknown, fallback: StorefrontSizeToken): StorefrontSizeToken {
  return STOREFRONT_SIZES.includes(value as StorefrontSizeToken) ? (value as StorefrontSizeToken) : fallback;
}

function readRounded(value: unknown, fallback: StorefrontSizeRounded): StorefrontSizeRounded {
  return STOREFRONT_ROUNDEDS.includes(value as StorefrontSizeRounded) ? (value as StorefrontSizeRounded) : fallback;
}

function readColor(value: unknown, fallback: string): string {
  return isHexColor(value) ? value.trim().toUpperCase() : fallback;
}

/** Band role: a hex, or `null` (follow card) for anything else. */
function readOptionalColor(value: unknown): string | null {
  return isHexColor(value) ? value.trim().toUpperCase() : null;
}

function readItemImage(value: unknown): StorefrontItemImage {
  const fallback = DEFAULT_STOREFRONT_APPEARANCE.itemImage;
  const source = (value ?? {}) as Partial<StorefrontItemImage>;

  return {
    visible: typeof source.visible === "boolean" ? source.visible : fallback.visible,
    size: readSize(source.size, fallback.size),
    shape: STOREFRONT_IMAGE_SHAPES.includes(source.shape as StorefrontImageShape)
      ? (source.shape as StorefrontImageShape)
      : fallback.shape,
  };
}

function readPrice(value: unknown): StorefrontPrice {
  const fallback = DEFAULT_STOREFRONT_APPEARANCE.price;
  const source = (value ?? {}) as Partial<StorefrontPrice>;

  return {
    showFromLabel:
      typeof source.showFromLabel === "boolean"
        ? source.showFromLabel
        : 
          fallback.showFromLabel,
  };
}

function readHeader(value: unknown): StorefrontHeader {
  const fallback = DEFAULT_STOREFRONT_APPEARANCE.header;
  const source = (value ?? {}) as Partial<StorefrontHeader>;

  return {
    density: STOREFRONT_HEADER_DENSITIES.includes(source.density as StorefrontHeaderDensity)
      ? (source.density as StorefrontHeaderDensity)
      : fallback.density,
    showBanner: typeof source.showBanner === "boolean" ? source.showBanner : fallback.showBanner,
  };
}

function readTitleCard(value: unknown, fallback: StorefrontTitleCard): StorefrontTitleCard {
  const source = (value ?? {}) as Partial<StorefrontTitleCard>;

  return {
    visible: typeof source.visible === "boolean" ? source.visible : fallback.visible,
    rounded: readRounded(source.rounded, fallback.rounded),
  };
}

function readMenu(value: unknown): StorefrontMenu {
  const fallback = DEFAULT_STOREFRONT_APPEARANCE.menu;
  const source = (value ?? {}) as Partial<StorefrontMenu>;

  return {
    rounded: readRounded(source.rounded, fallback.rounded),
    titleCard: readTitleCard(source.titleCard, fallback.titleCard),
  };
}

/**
 * Pre-group payloads INHERIT corner from `menu.rounded` — the field the
 * storefront used for category cards — so a chosen corner does not reset.
 */
function readCategoryList(
  value: unknown,
  menu: StorefrontMenu,
): StorefrontCategoryList {
  const fallback = DEFAULT_STOREFRONT_APPEARANCE.categoryList;
  const source = (value ?? {}) as Partial<StorefrontCategoryList>;
  const inheritedRounded = value == null ? menu.rounded : fallback.rounded;

  return {
    cardSize: readSize(source.cardSize, fallback.cardSize),
    rounded: readRounded(source.rounded, inheritedRounded),
    showDescription:
      typeof source.showDescription === "boolean"
        ? source.showDescription
        : fallback.showDescription,
  };
}

function readSupport(value: unknown): StorefrontSupport {
  const fallback = DEFAULT_STOREFRONT_APPEARANCE.support;
  const source = (value ?? {}) as Partial<StorefrontSupport>;

  return {
    visible: typeof source.visible === "boolean" ? source.visible : fallback.visible,
    size: readSize(source.size, fallback.size),
    content: STOREFRONT_SUPPORT_CONTENTS.includes(source.content as StorefrontSupportContent)
      ? (source.content as StorefrontSupportContent)
      : fallback.content,
    side: STOREFRONT_SUPPORT_SIDES.includes(source.side as StorefrontSupportSide)
      ? (source.side as StorefrontSupportSide)
      : fallback.side,
    rounded: readRounded(source.rounded, fallback.rounded),
    message: readSupportMessage(source.message),
  };
}

/** WhatsApp formatting markers, longest to shortest. */
const SUPPORT_MARKERS = ["```", "*", "_", "~"] as const;

/**
 * Rewrites standard Markdown into WhatsApp dialect. Mirrors zap-service-core
 * `sanitizeForWhatsApp` so `**bold**` does not show four asterisks in chat.
 */
export function sanitizeForWhatsApp(text: string): string {
  return text
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, (_match, label: string, url: string) => {
      const trimmed = label.trim();
      return !trimmed || trimmed === url ? url : `${trimmed}: ${url}`;
    })
    .replace(/^#{1,6}\s+(.+?)\s*$/gm, "*$1*")
    .replace(/\*\*([^*\n]+?)\*\*/g, "*$1*")
    .replace(/__([^_\n]+?)__/g, "*$1*")
    .replace(/~~([^~\n]+?)~~/g, "~$1~");
}

/**
 * Tightens formatting markers against wrapped text. WhatsApp only formats when
 * markers touch content. Runs AFTER {@link sanitizeForWhatsApp}; empty pairs
 * stay intact so merchant text is not rewritten away.
 */
export function tightenSupportMarkers(text: string): string {
  let tightened = text;

  for (const marker of SUPPORT_MARKERS) {
    const escaped = marker.replace(/\*/g, "\\*");
    const pair = new RegExp(`${escaped}([\\s\\S]*?)${escaped}`, "g");

    tightened = tightened.replace(pair, (match: string, inner: string) => {
      const core = inner.trim();
      if (!core) return match;

      const lead = inner.slice(0, inner.indexOf(core));
      const tail = inner.slice(lead.length + core.length);
      return `${lead}${marker}${core}${marker}${tail}`;
    });
  }

  return tightened;
}

/**
 * Message sanitized for URL length and WhatsApp formatting. Newlines become
 * spaces. Order: {@link sanitizeForWhatsApp} → {@link tightenSupportMarkers} →
 * collapse/trim → cap.
 */
function readSupportMessage(value: unknown): string {
  if (typeof value !== "string") return "";
  const tightened = tightenSupportMarkers(sanitizeForWhatsApp(value));
  return capSupportMessage(tightened.replace(/\s+/g, " ").trim());
}

/**
 * Caps the message by CODE POINTS (like Go runes). UTF-16 `slice` can split an
 * emoji and break `encodeURIComponent` — losing the whole button.
 */
export function capSupportMessage(text: string): string {
  const points = Array.from(text);
  if (points.length <= STOREFRONT_SUPPORT_MESSAGE_MAX) return text;
  return points.slice(0, STOREFRONT_SUPPORT_MESSAGE_MAX).join("");
}

/**
 * Reads anything and returns a VALID appearance.
 * 1. Unknown sections are dropped;
 * 2. Known sections missing from the payload append at the END with defaults;
 * 3. Bad colors/sizes fall back field-by-field (never the whole object).
 */
export function normalizeStorefrontAppearance(raw: unknown): StorefrontAppearance {
  const source = (raw ?? {}) as Partial<StorefrontAppearance>;
  const defaults = DEFAULT_STOREFRONT_APPEARANCE;

  const rawSections = Array.isArray(source.sections) ? source.sections : [];
  const seen = new Set<StorefrontSectionKey>();
  const sections: StorefrontSection[] = [];

  for (const entry of rawSections) {
    const key = (entry as StorefrontSection)?.key;
    if (!STOREFRONT_SECTION_KEYS.includes(key)) continue;
    if (seen.has(key)) continue;
    seen.add(key);

    const fallback = defaults.sections.find((section) => section.key === key)!;
    sections.push({
      key,
      visible:
        typeof (entry as StorefrontSection).visible === "boolean"
          ? (entry as StorefrontSection).visible
          : fallback.visible,
      size: readSize((entry as StorefrontSection).size, fallback.size),
      imageVisible:
        typeof (entry as StorefrontSection).imageVisible === "boolean"
          ? (entry as StorefrontSection).imageVisible
          : fallback.imageVisible,
      couponArt: STOREFRONT_COUPON_ARTS.includes((entry as StorefrontSection).couponArt)
        ? (entry as StorefrontSection).couponArt
        : fallback.couponArt,
      couponDisplay: STOREFRONT_COUPON_DISPLAYS.includes((entry as StorefrontSection).couponDisplay)
        ? (entry as StorefrontSection).couponDisplay
        : fallback.couponDisplay,
      rounded: readRounded((entry as StorefrontSection).rounded, fallback.rounded),
      titleCard: readTitleCard((entry as StorefrontSection).titleCard, fallback.titleCard),
    });
  }

  for (const fallback of defaults.sections) {
    if (!seen.has(fallback.key)) sections.push({ ...fallback });
  }

  const rawColors = (source.colors ?? {}) as Partial<StorefrontColors>;
  const menu = readMenu(source.menu);

  return {
    version: STOREFRONT_APPEARANCE_VERSION,
    sections,
    titleSize: readSize(source.titleSize, defaults.titleSize),
    categoryNavSize: readSize(source.categoryNavSize, defaults.categoryNavSize),
    arrowSize: readSize(source.arrowSize, defaults.arrowSize),
    cardSize: readSize(source.cardSize, defaults.cardSize),
    itemImage: readItemImage(source.itemImage),
    price: readPrice(source.price),
    header: readHeader(source.header),
    menu,
    categoryList: readCategoryList(source.categoryList, menu),
    support: readSupport(source.support),
    colors: {
      background: readColor(rawColors.background, defaults.colors.background),
      primary: readColor(rawColors.primary, defaults.colors.primary),
      secondary: readColor(rawColors.secondary, defaults.colors.secondary),
      cardBackground: readColor(rawColors.cardBackground, defaults.colors.cardBackground),
      border: readColor(rawColors.border, defaults.colors.border),
      cardText: readColor(rawColors.cardText, defaults.colors.cardText),
      text: readColor(rawColors.text, defaults.colors.text),
      titleCardBackground: readOptionalColor(rawColors.titleCardBackground),
      titleCardText: readOptionalColor(rawColors.titleCardText),
    },
  };
}
